import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "template-table-card" }
const _hoisted_2 = { class: "template-table-card__header" }
const _hoisted_3 = { class: "template-table-card__title" }
const _hoisted_4 = { class: "template-table-card__body" }
const _hoisted_5 = { class: "template-table-card__actions" }
const _hoisted_6 = ["disabled", "onClick"]
const _hoisted_7 = { class: "template-table-card__empty" }
const _hoisted_8 = { class: "template-table-card__error" }

import { computed } from 'vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { OrderTemplateSummaryEntity } from '../domains/entities/orderTemplate';
import { i18n } from '@/i18n';

type Props = {
  /** テーブルタイトル */
  title: string;
  /** 表示対象のテンプレート一覧 */
  templates: OrderTemplateSummaryEntity[];
  /** ローディング状態 */
  loading: boolean;
  /** エラー */
  errorMessage?: string | null;
  /** ゼロ件時のメッセージ */
  emptyMessage?: string;
  /** 処理中テンプレートID一覧 */
  processingTemplateIds?: string[];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderTemplateTableCard',
  props: {
    title: {},
    templates: {},
    loading: { type: Boolean },
    errorMessage: { default: null },
    emptyMessage: { default: undefined },
    processingTemplateIds: { default: (): string[] => [] }
  },
  emits: ["delete-click"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;

/**
 * テンプレート一覧カードの入力パラメータ
 */
const props = __props;

const resolvedEmptyMessage = computed(() => props.emptyMessage ?? (t('登録された書式はありません') as string));

const columns: string[] = ['120px', '500px', '200px', '200px', 'auto'];

/**
 * 削除ボタン押下イベントを親へ伝える
 */
const emits = __emit;

/**
 * テーブル表示状態を計算する
 */
const loadingStatus = computed<LoadingStatus>(() => {
  if (props.loading) {
    return { type: 'loading' };
  }
  if (props.errorMessage) {
    return { type: 'error', errorMessage: props.errorMessage };
  }
  if (props.templates.length === 0) {
    return { type: 'zero-match' };
  }
  return { type: 'success' };
});

/**
 * UNIXタイムスタンプを日付文字列（YYYY/MM/DD）に整形する
 * @param timestamp - UNIXタイム（秒またはミリ秒）
 */
const formatDate = (timestamp: number): string => {
  const isSeconds = timestamp < 1_000_000_000_000;
  const date = new Date(isSeconds ? timestamp * 1000 : timestamp);
  return date.toLocaleDateString('ja-JP');
};

/**
 * UNIXタイムスタンプを日時文字列（YYYY/MM/DD HH:mm）に整形する
 * @param timestamp - UNIXタイム（秒またはミリ秒）
 */
const formatDateTime = (timestamp: number): string => {
  const isSeconds = timestamp < 1_000_000_000_000;
  const date = new Date(isSeconds ? timestamp * 1000 : timestamp);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * 指定テンプレートが処理中か判定する
 * @param templateId - 判定対象のテンプレートID
 */
const isProcessing = (templateId: string): boolean => {
  return props.processingTemplateIds.includes(templateId);
};

/**
 * 編集リンクの遷移先を構築する
 * @param template - 表示中のテンプレート概要
 */
const buildEditRoute = (template: OrderTemplateSummaryEntity) => {
  return {
    name: 'order-template-edit',
    query: {
      templateId: template.templateId,
    },
  } as const;
};

/**
 * 削除ボタン押下時のハンドラ
 * @param templateId - 削除対象のテンプレートID
 */
const handleDeleteClick = (templateId: string) => {
  emits('delete-click', templateId);
};

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.title), 1)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(EzTable, {
        "grid-template-columns": columns,
        "loading-status": loadingStatus.value
      }, _createSlots({
        header: _withCtx(() => [
          _createVNode(EzHeader, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('登録日')), 1)
            ], undefined, true),
            _: 1
          }),
          _createVNode(EzHeader, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('ファイル名')), 1)
            ], undefined, true),
            _: 1
          }),
          _createVNode(EzHeader, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('最終更新日時')), 1)
            ], undefined, true),
            _: 1
          }),
          _createVNode(EzHeader, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('最終更新者')), 1)
            ], undefined, true),
            _: 1
          }),
          _createVNode(EzHeader)
        ]),
        "zero-match": _withCtx(() => [
          _createElementVNode("div", _hoisted_7, _toDisplayString(resolvedEmptyMessage.value), 1)
        ]),
        error: _withCtx(() => [
          _createElementVNode("div", _hoisted_8, _toDisplayString(_ctx.errorMessage || _unref(t)('データの取得に失敗しました')), 1)
        ]),
        _: 2
      }, [
        (loadingStatus.value.type === 'success')
          ? {
              name: "body",
              fn: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.templates, (template) => {
                  return (_openBlock(), _createBlock(EzTableRow, {
                    key: template.templateId,
                    "grid-template-columns": columns
                  }, {
                    default: _withCtx(() => [
                      _createVNode(EzCell, null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(formatDate(template.createAt)), 1)
                        ], undefined, true),
                        _: 2
                      }, 1024),
                      _createVNode(EzCell, null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(template.fileOriginalName), 1)
                        ], undefined, true),
                        _: 2
                      }, 1024),
                      _createVNode(EzCell, null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(formatDateTime(template.updateAt)), 1)
                        ], undefined, true),
                        _: 2
                      }, 1024),
                      _createVNode(EzCell, null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(template.updatedByName ?? template.updatedBy), 1)
                        ], undefined, true),
                        _: 2
                      }, 1024),
                      _createVNode(EzCell, { "force-center": "" }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_5, [
                            _createVNode(_component_RouterLink, {
                              to: buildEditRoute(template),
                              class: "template-table-card__button template-table-card__button--edit"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_unref(t)('編集')), 1)
                              ], undefined, true),
                              _: 2
                            }, 1032, ["to"]),
                            _createElementVNode("button", {
                              type: "button",
                              class: "template-table-card__button template-table-card__button--delete",
                              disabled: isProcessing(template.templateId),
                              onClick: ($event: any) => (handleDeleteClick(template.templateId))
                            }, _toDisplayString(_unref(t)('削除')), 9, _hoisted_6)
                          ])
                        ], undefined, true),
                        _: 2
                      }, 1024)
                    ], undefined, true),
                    _: 2
                  }, 1024))
                }), 128))
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["loading-status"])
    ])
  ]))
}
}

})